/**
 * Chọn quy trình sản xuất của sản phẩm trong một modal (thay 4 ô nhóm Trước in / In ấn / Sau in / Thành phẩm).
 * - 5 nút lọc + ô tìm, bấm dòng để chọn / bỏ chọn; công đoạn chọn trước đứng trước.
 * - Thứ tự quy trình kéo thả được (trong modal và ngay trên form); có nút sắp theo chuẩn công nghệ khi cần.
 */
import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ListOrdered, Plus, Search, Wand2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { emsTabClass } from "@/components/ui/ems-tabs";

export type ProcessOption = { id: string; label: string; original?: any };

export const PROCESS_GROUPS = [
  { key: "TRUOC_IN", label: "Trước in", dot: "bg-purple-500", chip: "bg-purple-50 text-purple-800 border-purple-200" },
  { key: "IN", label: "In ấn", dot: "bg-blue-500", chip: "bg-blue-50 text-blue-800 border-blue-200" },
  { key: "SAU_IN", label: "Sau in", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "THANH_PHAM", label: "Thành phẩm", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
] as const;

const groupMeta = (key: string) => PROCESS_GROUPS.find((g) => g.key === key) ?? PROCESS_GROUPS[3];

function useDndSensors() {
  // Kéo tối thiểu 5px để bấm nút xoá trên thẻ không bị hiểu là kéo
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

function SortableChip({
  id,
  index,
  label,
  groupKey,
  onRemove,
}: {
  id: string;
  index: number;
  label: string;
  groupKey: string;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const meta = groupMeta(groupKey);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid="process-chip"
      data-process-id={id}
      className={cn(
        "flex items-center gap-1 h-7 pl-1 pr-1.5 rounded-md border text-[11px] font-medium select-none",
        meta.chip,
        isDragging && "shadow-lg ring-2 ring-emerald-400 z-10",
      )}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 p-0.5"
        aria-label={`Kéo để đổi thứ tự ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <span className="tabular-nums text-slate-500">{index + 1}.</span>
      <span className="truncate max-w-[180px]">{label}</span>
      <button
        type="button"
        className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
        aria-label={`Bỏ ${label}`}
        onClick={onRemove}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function SortableRow({
  id,
  index,
  label,
  groupKey,
  onRemove,
}: {
  id: string;
  index: number;
  label: string;
  groupKey: string;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const meta = groupMeta(groupKey);
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid="process-order-row"
      data-process-id={id}
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 rounded-md border bg-white text-xs",
        isDragging && "shadow-lg ring-2 ring-emerald-400 z-10",
      )}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
        aria-label={`Kéo để đổi thứ tự ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="w-5 text-right tabular-nums font-semibold text-slate-500">{index + 1}</span>
      <span className={cn("w-2 h-2 rounded-full shrink-0", meta.dot)} />
      <span className="flex-1 truncate font-medium text-slate-800">{label}</span>
      <span className="text-[10px] text-slate-400 hidden sm:inline">{meta.label}</span>
      <button type="button" className="rounded p-0.5 hover:bg-slate-100" aria-label={`Bỏ ${label}`} onClick={onRemove}>
        <X className="h-3.5 w-3.5 text-slate-500" />
      </button>
    </li>
  );
}

interface ProcessRoutingPickerProps {
  options: ProcessOption[];
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  /** Nhóm công đoạn (TRUOC_IN / IN / SAU_IN / THANH_PHAM) */
  groupOf: (opt: ProcessOption) => string;
  /** Sắp lại theo chuỗi công nghệ chuẩn (tuỳ chọn) */
  onNormalize?: () => void;
  isLoading?: boolean;
  canAdd?: boolean;
  onQuickAdd?: (name: string, groupKey: string) => void;
  hasError?: boolean;
  errorMessages?: string[];
}

export function ProcessRoutingPicker({
  options,
  selectedIds,
  onChange,
  groupOf,
  onNormalize,
  isLoading,
  canAdd,
  onQuickAdd,
  hasError,
  errorMessages = [],
}: ProcessRoutingPickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selectedIds);
  const [filter, setFilter] = useState<string>("ALL");
  const [search, setSearch] = useState("");
  const sensors = useDndSensors();

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const groupById = useMemo(() => new Map(options.map((o) => [o.id, groupOf(o)])), [options, groupOf]);

  // Công đoạn vừa tạo nhanh từ modal được thêm vào danh sách đã chọn ở form → cập nhật bản nháp đang mở
  useEffect(() => {
    if (!open) return;
    setDraft((prev) => [...prev, ...selectedIds.filter((id) => !prev.includes(id))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const openPicker = () => {
    setDraft(selectedIds);
    setSearch("");
    setFilter("ALL");
    setOpen(true);
  };

  const toggleDraft = (id: string) =>
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return options.filter((o) => {
      if (filter !== "ALL" && groupById.get(o.id) !== filter) return false;
      if (!q) return true;
      const code = String(o.original?.stepId || o.original?.code || "").toLowerCase();
      return o.label.toLowerCase().includes(q) || code.includes(q);
    });
  }, [options, filter, search, groupById]);

  const countFor = (key: string) => {
    const total = key === "ALL" ? options.length : options.filter((o) => groupById.get(o.id) === key).length;
    const picked = key === "ALL" ? draft.length : draft.filter((id) => groupById.get(id) === key).length;
    return { total, picked };
  };

  const reorder = (ids: string[], e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return null;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return null;
    return arrayMove(ids, from, to);
  };

  const filters = [{ key: "ALL", label: "Tất cả" }, ...PROCESS_GROUPS.map((g) => ({ key: g.key, label: g.label }))];
  const labelOf = (id: string) => byId.get(id)?.label ?? id;

  return (
    <div className="space-y-1.5">
      <div
        id="processIds"
        data-testid="process-routing-field"
        className={cn(
          "min-h-[44px] w-full rounded-lg border bg-white p-2 flex flex-wrap items-center gap-1.5",
          hasError ? "border-rose-500 ring-1 ring-rose-400 bg-rose-50/20" : "border-slate-200",
        )}
      >
        {selectedIds.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => {
              const next = reorder(selectedIds, e);
              if (next) onChange(next);
            }}
          >
            <SortableContext items={selectedIds} strategy={rectSortingStrategy}>
              {selectedIds.map((id, i) => (
                <SortableChip
                  key={id}
                  id={id}
                  index={i}
                  label={labelOf(id)}
                  groupKey={groupById.get(id) ?? "THANH_PHAM"}
                  onRemove={() => onChange(selectedIds.filter((x) => x !== id))}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <span className="text-xs text-slate-400 px-1">Chưa chọn công đoạn nào</span>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="process-picker-open"
          className="h-7 px-2 text-[11px] text-emerald-700 bg-emerald-50/70 hover:bg-emerald-100 border-dashed border-emerald-300 gap-1"
          onClick={openPicker}
        >
          <Plus className="h-3 w-3" />
          {selectedIds.length === 0 ? "Chọn công đoạn" : "Thêm / bỏ công đoạn"}
        </Button>
        {onNormalize && selectedIds.length > 1 && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid="process-normalize"
            className="h-7 px-2 text-[11px] text-slate-500 gap-1"
            title="Sắp lại theo chuỗi công nghệ: Trước in → In → Sau in → Thành phẩm"
            onClick={onNormalize}
          >
            <Wand2 className="h-3 w-3" />
            Sắp theo chuẩn
          </Button>
        )}
      </div>
      {selectedIds.length > 1 && (
        <p className="text-[11px] text-slate-400">Kéo thẻ để đổi thứ tự công đoạn.</p>
      )}
      {errorMessages.map((m) => (
        <p key={m} className="text-[11px] text-rose-500 font-medium">
          {m}
        </p>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl w-[calc(100vw-1rem)] p-0 gap-0 max-h-[92vh] flex flex-col" data-testid="process-picker-modal">
          <DialogHeader className="px-4 pt-4 pb-2 border-b">
            <DialogTitle className="text-base">Chọn công đoạn sản xuất</DialogTitle>
            <DialogDescription className="text-xs">
              Bấm vào dòng để chọn hoặc bỏ. Công đoạn chọn trước đứng trước; kéo ở cột Thứ tự để đổi chỗ.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col md:flex-row min-h-0 flex-1">
            <div className="flex flex-col min-h-0 flex-1 md:border-r">
              <div className="px-3 pt-3 space-y-2">
                <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist">
                  {filters.map((f) => {
                    const c = countFor(f.key);
                    const active = filter === f.key;
                    const meta = f.key === "ALL" ? null : groupMeta(f.key);
                    return (
                      <button
                        key={f.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        data-testid={`process-filter-${f.key}`}
                        onClick={() => setFilter(f.key)}
                        className={emsTabClass(active, { size: "sm" })}
                      >
                        {meta && <span className={cn("w-2 h-2 rounded-full", meta.dot)} />}
                        {f.label}
                        <span className={cn("tabular-nums text-[10px]", active ? "text-primary/70" : "text-slate-400")}>
                          {c.picked}/{c.total}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Tìm tên hoặc mã công đoạn..."
                      className="h-8 pl-7 text-xs"
                      data-testid="process-picker-search"
                    />
                  </div>
                  {canAdd && onQuickAdd && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs gap-1"
                      data-testid="process-picker-create"
                      onClick={() => onQuickAdd(search.trim(), filter === "ALL" ? "" : filter)}
                    >
                      <Plus className="h-3 w-3" /> Tạo mới
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-[220px] max-h-[48vh] md:max-h-[56vh] overflow-y-auto px-3 py-2">
                {isLoading ? (
                  <div className="text-center py-6 text-xs text-slate-400">Đang tải...</div>
                ) : visible.length === 0 ? (
                  <div className="text-center py-6 text-xs text-slate-400">Không tìm thấy công đoạn</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white z-[1]">
                      <tr className="text-left text-[11px] text-slate-500 border-b">
                        <th className="w-8 py-1.5" />
                        <th className="py-1.5 font-medium">Công đoạn</th>
                        <th className="py-1.5 font-medium hidden sm:table-cell">Nhóm</th>
                        <th className="py-1.5 font-medium text-right w-14">Thứ tự</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((o) => {
                        const pos = draft.indexOf(o.id);
                        const picked = pos >= 0;
                        const meta = groupMeta(groupById.get(o.id) ?? "THANH_PHAM");
                        return (
                          <tr
                            key={o.id}
                            data-testid="process-picker-row"
                            data-process-id={o.id}
                            aria-selected={picked}
                            // Giữ con trỏ ở ô tìm để chọn xong gõ tiếp được (FB-2FOD6S)
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => toggleDraft(o.id)}
                            className={cn(
                              "cursor-pointer border-b border-slate-50 select-none",
                              picked ? "bg-emerald-50 hover:bg-emerald-100/70" : "hover:bg-slate-50",
                            )}
                          >
                            <td className="py-1.5 pl-1">
                              <Checkbox checked={picked} className="h-3.5 w-3.5 pointer-events-none" tabIndex={-1} />
                            </td>
                            <td className={cn("py-1.5", picked && "font-semibold text-emerald-900")}>
                              {o.label}
                              {o.original?.stepId && (
                                <span className="ml-1.5 text-[10px] text-slate-400 font-normal">{o.original.stepId}</span>
                              )}
                            </td>
                            <td className="py-1.5 hidden sm:table-cell">
                              <span className="inline-flex items-center gap-1 text-slate-500">
                                <span className={cn("w-2 h-2 rounded-full", meta.dot)} />
                                {meta.label}
                              </span>
                            </td>
                            <td className="py-1.5 text-right pr-1 tabular-nums font-semibold text-emerald-700">
                              {picked ? pos + 1 : ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="md:w-72 shrink-0 flex flex-col min-h-0 border-t md:border-t-0 bg-slate-50/60">
              <div className="px-3 py-2 flex items-center justify-between border-b">
                <span className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1">
                  <ListOrdered className="h-3.5 w-3.5" /> Thứ tự quy trình ({draft.length})
                </span>
                {draft.length > 0 && (
                  <button type="button" className="text-[11px] text-slate-500 hover:text-rose-600" onClick={() => setDraft([])}>
                    Bỏ hết
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-2 max-h-[28vh] md:max-h-[56vh]">
                {draft.length === 0 ? (
                  <p className="text-[11px] text-slate-400 text-center py-6">Chưa chọn công đoạn</p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => {
                      const next = reorder(draft, e);
                      if (next) setDraft(next);
                    }}
                  >
                    <SortableContext items={draft} strategy={verticalListSortingStrategy}>
                      <ol className="space-y-1" data-testid="process-order-list">
                        {draft.map((id, i) => (
                          <SortableRow
                            key={id}
                            id={id}
                            index={i}
                            label={labelOf(id)}
                            groupKey={groupById.get(id) ?? "THANH_PHAM"}
                            onRemove={() => setDraft((prev) => prev.filter((x) => x !== id))}
                          />
                        ))}
                      </ol>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="px-4 py-3 border-t gap-2 sm:gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
              Huỷ
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="process-picker-apply"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
            >
              Xong ({draft.length} công đoạn)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
